import { createFileRoute } from "@tanstack/react-router";

import { CoachListGrid } from "~/components/coach-list-grid";
import { FaqList } from "~/components/faq-list";
import { BottomCTA } from "~/components/feature/bottom-cta";
import { BreakdownIntro } from "~/components/feature/breakdown-intro";
import { MidListCTA } from "~/components/feature/mid-list-cta";
import { MarkdownContent } from "~/components/markdown-content";
import { Breadcrumb } from "~/components/ui/breadcrumb";
import { Heading } from "~/components/ui/heading";
import { Subheading } from "~/components/ui/subheading";
import type { CoachOverview } from "~/lib/coach-grouping";
import { getCityLabelFromValue } from "~/lib/functions/cities";
import { getCitySubcategoryPageContent } from "~/lib/functions/coach-group-seo";
import { getSubcategoryBySlug } from "~/lib/functions/expertise";
import { coachQueryOptions } from "~/lib/query-options";
import { coachListJsonLdScripts } from "~/lib/shared/coach-list-head";
import { createBrandMeta } from "~/lib/shared/meta";

export const Route = createFileRoute(
  "/_public/coaches/city/$slug/subcategory/$subcategorySlug/",
)({
  loader: async ({ context, params }) => {
    const { slug: citySlug, subcategorySlug } = params;

    const [coaches, subcategory, cityLabel, page] = await Promise.all([
      context.queryClient.ensureQueryData(
        coachQueryOptions.bySubcategoryAndCity(subcategorySlug, citySlug),
      ),
      getSubcategoryBySlug({ data: { slug: subcategorySlug } }),
      getCityLabelFromValue({ data: { citySlug } }),
      getCitySubcategoryPageContent({ data: { citySlug, subcategorySlug } }),
    ]);

    await context.queryClient.ensureQueryData(
      coachQueryOptions.bySubcategoryAndCity(subcategorySlug, citySlug),
    );

    return {
      coaches: coaches as CoachOverview[],
      subcategory,
      citySlug,
      cityLabel,
      subcategorySlug,
      page,
    };
  },
  head: ({ params, loaderData }) => {
    const { subcategory, cityLabel, page } = loaderData || {};
    const subcategoryName =
      subcategory?.listingName || subcategory?.name || params.subcategorySlug;
    const cityName = cityLabel || params.slug;

    const pageUrl = `https://dreamcoachmatch.com/coaches/city/${params.slug}/subcategory/${params.subcategorySlug}`;
    const ogImage = `https://dreamcoachmatch.com/img/og/city-${params.slug}.jpg`;
    const metaConfig = createBrandMeta({
      title: page?.seoTitle ?? `${subcategoryName} Coaches in ${cityName}`,
      description:
        page?.metaDescription ??
        `Find ${subcategoryName} coaches available in ${cityName} on Dream Coach Match. Connect with coaches for personal and professional growth.`,
      ogImage,
      ogUrl: pageUrl,
      canonical: pageUrl,
      // City×subcategory combo pages are retired from search (issue #1010):
      // they fragment the index into thin near-duplicate pages. The routes
      // stay live for visitors, but always noindex + follow so crawlers pass
      // through to the coach profiles. Self-canonical, never cross-page —
      // noindex + cross-canonical would be contradictory signals.
      noindexFollow: true,
    });

    return {
      ...metaConfig,
      scripts: coachListJsonLdScripts({
        breadcrumbs: [
          { name: "Home", url: "https://dreamcoachmatch.com" },
          { name: "Coaches", url: "https://dreamcoachmatch.com/coaches" },
          { name: "Cities", url: "https://dreamcoachmatch.com/coaches/city" },
          {
            name: cityName,
            url: `https://dreamcoachmatch.com/coaches/city/${params.slug}`,
          },
          { name: subcategoryName, url: pageUrl },
        ],
        faqItems: page?.faqItems,
      }),
    };
  },
  component: CitySubcategoryCoachesPage,
});

function CitySubcategoryCoachesPage() {
  const { coaches, subcategory, citySlug, cityLabel, subcategorySlug, page } =
    Route.useLoaderData();

  const subcategoryName =
    subcategory?.listingName || subcategory?.name || "Specialized";
  const cityName = cityLabel || citySlug;
  const pageTitle = `${subcategoryName} Coaches in ${cityName}`;
  const combinedBreakdownName = `${subcategoryName} in ${cityName}`;

  return (
    <div className="mx-auto max-w-7xl px-4 py-12">
      <Breadcrumb
        className="justify-center"
        items={[
          { label: "Home", href: "/" },
          { label: "Coaches", href: "/coaches" },
          { label: "Cities", href: "/coaches/city" },
          { label: cityName, href: `/coaches/city/${citySlug}` },
          { label: subcategoryName },
        ]}
      />
      <Heading
        preHeadline="Dream Coach Match"
        headline={pageTitle}
        subheadline={`Find specialized ${subcategoryName} coaches in ${cityName} to help you achieve your personal and professional goals.`}
      />
      <BreakdownIntro
        breakdownName={combinedBreakdownName}
        breakdownTypeSlug={`${subcategorySlug}-in-${citySlug}`}
        ctaButtonTextTemplate="Find Your Coach"
        utmCampaignPrefix="city_subcategory_coaches"
      />
      {coaches.length > 0 && (
        <Subheading className="mt-12 text-center">
          {`Coaches in ${cityName} · ${subcategoryName}`}
        </Subheading>
      )}
      <CoachListGrid
        coaches={coaches}
        emptyMessage={`No ${subcategoryName} coaches found in ${cityName} yet.`}
        midListCta={
          <MidListCTA
            breakdownName={combinedBreakdownName}
            breakdownTypeSlug={`${subcategorySlug}-in-${citySlug}`}
            preLinkText="Need help choosing?"
            ctaLinkTextTemplate="Let us match you with the ideal coach!"
            utmCampaignPrefix="city_subcategory_coaches_mid"
          />
        }
      />
      <BottomCTA
        itemCount={coaches.length}
        itemTypePlural="coaches"
        breakdownName={combinedBreakdownName}
        breakdownTypeSlug={`${subcategorySlug}-in-${citySlug}`}
        joinAsLinkUrl="/page/coach"
        joinAsLinkText="Join as a coach."
        mainCtaSubTextTemplate={`Ready to connect with a specialized ${subcategoryName} coach in ${cityName}?`}
        mainCtaButtonTextTemplate="Try our free Matchmaker tool"
        utmCampaignPrefix="city_subcategory_coaches_bottom"
      />
      {page?.contentMd && (
        <div className="mx-auto prose mt-12">
          <MarkdownContent content={page.contentMd} demoteH1 />
        </div>
      )}
      {page?.faqItems && page.faqItems.length > 0 && (
        <FaqList items={page.faqItems} className="mx-auto max-w-2xl" />
      )}
    </div>
  );
}
